import type { RefObject } from 'react';
import { useEffect } from 'react';
import { breakpoints, Inline, Input, Stack } from '@etg/wings';
import { useSearch, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { useFormContext, useWatch } from 'react-hook-form';
import { m as motion } from 'framer-motion';
import { resetAllFilters } from '../../../pages/result/components/search-filters/utils/searchFilters';
import { stopoverFields } from '../constants/formFields';
import {
  STOPOVER_TYPES,
  SHORT_STOPOVER as SHORT_FILTER,
  ONE_DAY_STOPOVER as ONE_DAY_FILTER,
  ONE_NIGHT_STOPOVER as ONE_NIGHT_FILTER,
  TWO_NIGHTS_STOPOVER as TWO_NIGHTS_FILTER,
} from '../../../constants/searchFiltersConstants';
import StopoverCheckbox from './StopoverCheckbox';

const { ENABLED, ONE_DAY, ONE_NIGHT, SHORT, TWO_NIGHTS } = stopoverFields;

const fieldSetStyles = css`
  border: none;
  padding-top: 1rem;
`;

const checkBoxStyles = css`
  margin-top: 1rem;
`;

const fieldsetVariants = {
  visible: { y: 0, opacity: 1, height: 'auto' },
  hidden: { y: -25, opacity: 0, height: 0 },
};

interface StopoverSelectNewProps {
  withTopSpacing?: boolean;
  checkboxRef?: RefObject<HTMLInputElement>;
}

const StopoverSelectNew = ({ withTopSpacing, checkboxRef }: StopoverSelectNewProps) => {
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const { watch, setValue, control } = useFormContext();
  const isTopStopoverControlSelected = useWatch({ control, name: ENABLED });
  const [
    isShortStopoverSelected,
    isOneDayStopoverSelected,
    isOneNightStopoverSelected,
    isTwoNightsStopoverSelected,
  ] = watch([SHORT, ONE_DAY, ONE_NIGHT, TWO_NIGHTS]);
  const stopoversCollection = [
    isShortStopoverSelected,
    isOneDayStopoverSelected,
    isOneNightStopoverSelected,
    isTwoNightsStopoverSelected,
  ];
  const updateAllStopovers = (isStopOverEnabled: boolean) => {
    setValue(SHORT, isStopOverEnabled);
    setValue(ONE_DAY, isStopOverEnabled);
    setValue(ONE_NIGHT, isStopOverEnabled);
    setValue(TWO_NIGHTS, isStopOverEnabled);
  };

  const updateStopoverSearchData = (stopover: string) => {
    if (!setSearchData) {
      return;
    }

    const stopoverTypes = currentSearchData?.filters?.[STOPOVER_TYPES];
    const isStopoverEnabled = stopoverTypes?.includes(stopover);
    const filteredStopovers = stopoverTypes?.filter((item) => item !== stopover);
    const updatedStopovers = Array.isArray(stopoverTypes)
      ? [...stopoverTypes, stopover]
      : [stopover];

    if (!filteredStopovers?.length) {
      setSearchData({
        filters: {
          ...currentSearchData?.filters,
          [STOPOVER_TYPES]: null,
        },
      });
      return;
    }

    setSearchData({
      filters: {
        ...currentSearchData?.filters,
        [STOPOVER_TYPES]: isStopoverEnabled ? filteredStopovers : updatedStopovers,
      },
    });
  };

  const updateAllStopoversSearchData = () => {
    if (!setSearchData) {
      return;
    }

    if (isTopStopoverControlSelected) {
      setSearchData({ filters: { ...resetAllFilters(currentSearchData?.filters) } });
      return;
    }

    setSearchData({
      filters: {
        ...currentSearchData?.filters,
        [STOPOVER_TYPES]: [SHORT_FILTER, ONE_DAY_FILTER, ONE_NIGHT_FILTER, TWO_NIGHTS_FILTER],
      },
    });
  };

  useEffect(() => {
    if (stopoversCollection.every((stopover) => !stopover)) {
      setValue(ENABLED, false);
    }
  });

  return (
    <div>
      <Inline className={withTopSpacing ? checkBoxStyles : undefined} spacing={8}>
        <StopoverCheckbox
          data-testid={`${ENABLED}-input`}
          fieldName={ENABLED}
          id="stopoverCheckbox"
          onUpdate={() => {
            updateAllStopovers(!isTopStopoverControlSelected);
            updateAllStopoversSearchData();
          }}
          ref={checkboxRef}
        />
        <Input.Label htmlFor="stopoverCheckbox">{t('Searchform.Stopover.City')}</Input.Label>
      </Inline>
      <motion.div
        animate={isTopStopoverControlSelected ? 'visible' : 'hidden'}
        aria-hidden={!isTopStopoverControlSelected}
        // @ts-expect-error: 'inert' is supported in html, even though typescript doesn't recognize it
        inert={!isTopStopoverControlSelected ? 'true' : undefined}
        initial="hidden"
        style={{
          pointerEvents: isTopStopoverControlSelected ? 'auto' : 'none',
          overflow: 'hidden',
        }}
        transition={{ duration: 0.2, type: 'tween' }}
        variants={fieldsetVariants}
      >
        <fieldset className={fieldSetStyles}>
          <Stack spacing={8}>
            <legend>{t('Searchform.Stopover.LengthOfStay.Text')}</legend>
            <Inline collapseBelow={breakpoints._768} spacing={16}>
              <Inline spacing={0}>
                <StopoverCheckbox
                  data-testid={`${SHORT}-input`}
                  fieldName={SHORT}
                  id="shortStopCheckboxSearchForm"
                  onUpdate={() => {
                    updateStopoverSearchData(SHORT_FILTER);
                  }}
                />
                <Input.Label htmlFor="shortStopCheckboxSearchForm">
                  {t('Searchform.Stopover.ShortStop')}
                </Input.Label>
              </Inline>

              <Inline spacing={0}>
                <StopoverCheckbox
                  data-testid={`${ONE_DAY}-input`}
                  fieldName={ONE_DAY}
                  id="oneDayStopCheckboxSearchForm"
                  onUpdate={() => {
                    updateStopoverSearchData(ONE_DAY_FILTER);
                  }}
                />
                <Input.Label htmlFor="oneDayStopCheckboxSearchForm">
                  {t('Searchform.Stopover.OneDayStop')}
                </Input.Label>
              </Inline>

              <Inline spacing={0}>
                <StopoverCheckbox
                  data-testid={`${ONE_NIGHT}-input`}
                  fieldName={ONE_NIGHT}
                  id="oneNightStopCheckboxSearchForm"
                  onUpdate={() => {
                    updateStopoverSearchData(ONE_NIGHT_FILTER);
                  }}
                />
                <Input.Label htmlFor="oneNightStopCheckboxSearchForm">
                  {t('Searchform.Stopover.OneNightStop')}
                </Input.Label>
              </Inline>

              <Inline spacing={0}>
                <StopoverCheckbox
                  data-testid={`${TWO_NIGHTS}-input`}
                  fieldName={TWO_NIGHTS}
                  id="twoNightsStopCheckboxSearchForm"
                  onUpdate={() => {
                    updateStopoverSearchData(TWO_NIGHTS_FILTER);
                  }}
                />
                <Input.Label htmlFor="twoNightsStopCheckboxSearchForm">
                  {t('Searchform.Stopover.TwoNightsStop')}
                </Input.Label>
              </Inline>
            </Inline>
          </Stack>
        </fieldset>
      </motion.div>
    </div>
  );
};

export default StopoverSelectNew;
